import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, DollarSign, Sun, Utensils, Plane, Users,
  Car, Shirt, Wifi, Coffee, Heart, Dumbbell,
  Building, Users as UsersIcon, Calendar, Home
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { locationsTranslations } from '../translations/locations';
import { useManagedContent } from '../hooks/useManagedContent';
import { useManagedImages } from '../hooks/useManagedImages';
import { pageImageConfig } from '../content/pageImageConfig';
import '../App.css';

const locationIds = [5, 1, 2, 3, 4];
const locationImages = [
  'https://i.postimg.cc/Pf6Vg1hB/Chat-GPT-Image-Jan-20-2026-01-45-19-PM.png',
  'https://i.postimg.cc/wjDVjNK0/Chat-GPT-Image-Jan-20-2026-01-43-03-PM.png',
  'https://i.postimg.cc/YSkxGSMS/Chat-GPT-Image-Jan-20-2026-01-43-18-PM.png',
  'https://i.postimg.cc/W10n1ZRH/Chat-GPT-Image-Jan-20-2026-01-45-12-PM.png',
  'https://i.postimg.cc/YCQ3CFcd/Chat-GPT-Image-Jan-20-2026-01-45-16-PM.png',
];
const amenityIcons = [
  [Car, Shirt, Dumbbell, Home, Sun, Coffee],
  [Car, Shirt, Dumbbell, Wifi, Coffee, Heart],
  [Car, Shirt, UsersIcon, Heart, Dumbbell, Building],
  [Car, Shirt, Dumbbell, Calendar, Building, Heart],
  [Car, Shirt, Dumbbell, Coffee, Building, Home],
];
const benefitIcons = [MapPin, DollarSign, Sun, Utensils, Plane, Users];

function Locations() {
  const [expandedCard, setExpandedCard] = useState(null);
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'locations',
    language,
    defaultTranslations: locationsTranslations
  });
  const images = useManagedImages({
    pageKey: 'locations',
    defaultImages: pageImageConfig.locations
  });

  const locations = useMemo(() => t.locations.map((loc, i) => ({
    ...loc,
    id: locationIds[i],
    image: locationImages[i],
    amenities: loc.amenities.map((label, j) => ({ icon: amenityIcons[i][j], label })),
  })), [t]);

  const benefits = useMemo(() => t.benefits.items.map((item, i) => ({
    ...item,
    icon: benefitIcons[i],
  })), [t]);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div className="App">
      <Header />
      
      {/* Hero Section */}
      <section className="locations-hero">
        <div className="locations-hero-background">
          <img 
            src={images.hero}
            alt="Bulgaria sports facilities"
            loading="lazy"
          />
        </div>
        <div className="locations-hero-overlay"></div>
        <div className="locations-hero-content">
          <motion.div 
            className="locations-hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="breadcrumb">
              <Link to="/">{t.hero.home}</Link>
              <span> / </span>
              <span>{t.hero.locations}</span>
            </nav>
            <h1 className="locations-hero-heading">{t.hero.heading}</h1>
            <h2 className="locations-hero-subheading">{t.hero.subheading}</h2>
            <p className="locations-hero-tagline">{t.hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Location Cards Section */}
      <section className="locations-main-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.main.heading}
          </motion.h2>
          
          <div className="locations-cards-grid">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                className="location-detail-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="location-detail-image">
                  <img 
                    src={location.image} 
                    alt={location.name}
                    loading="lazy"
                  />
                  <div className="location-detail-overlay">
                    <span>{t.main.viewGallery}</span>
                  </div>
                </div>
                
                <div className="location-detail-content">
                  <div className="location-badge">
                    <MapPin size={16} />
                    <span>{location.location}</span>
                  </div>
                  
                  <div className="sports-tags">
                    {location.sports.map((sport, idx) => (
                      <span key={idx} className="sport-tag">{sport}</span>
                    ))}
                  </div>
                  
                  <h3 className="location-detail-name">{location.name}</h3>
                  <p className="location-detail-description">{location.description}</p>
                  
                  {expandedCard === location.id && (
                    <motion.div
                      className="location-details-expanded"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <div className="facility-details">
                        <h4>{t.main.facilities}</h4>
                        <ul>
                          {location.facilities.map((facility, idx) => (
                            <li key={idx}>{facility}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="facility-specs">
                        <p><strong>{t.main.capacity}</strong> {location.capacity}</p>
                        <p><strong>{t.main.surface}</strong> {location.surface}</p>
                        <p><strong>{t.main.availableSports}</strong> {location.availableSports}</p>
                      </div>
                      
                      <div className="amenities-grid">
                        {location.amenities.map((amenity, idx) => {
                          const IconComponent = amenity.icon;
                          return (
                            <div key={idx} className="amenity-item">
                              <IconComponent size={24} color="#C49818" />
                              <span>{amenity.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                  
                  <div className="location-detail-buttons">
                    {location.id === 1 ? (
                      <Link to="/locations/sofia-mladost">
                        <motion.button
                          className="btn-view-details"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t.main.viewDetails}
                        </motion.button>
                      </Link>
                    ) : location.id === 2 ? (
                      <Link to="/locations/sofia-nsa">
                        <motion.button
                          className="btn-view-details"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t.main.viewDetails}
                        </motion.button>
                      </Link>
                    ) : location.id === 3 ? (
                      <Link to="/locations/plovdiv">
                        <motion.button
                          className="btn-view-details"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t.main.viewDetails}
                        </motion.button>
                      </Link>
                    ) : location.id === 4 ? (
                      <Link to="/locations/sofia-boyana">
                        <motion.button
                          className="btn-view-details"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t.main.viewDetails}
                        </motion.button>
                      </Link>
                    ) : location.id === 5 ? (
                      <Link to="/locations/kranevo">
                        <motion.button
                          className="btn-view-details"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {t.main.viewDetails}
                        </motion.button>
                      </Link>
                    ) : (
                      <motion.button
                        className="btn-view-details"
                        onClick={() => toggleCard(location.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {expandedCard === location.id ? t.main.hideDetails : t.main.viewDetails}
                      </motion.button>
                    )}
                    <Link to="/book">
                      <motion.button
                        className="btn-book-facility"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t.main.bookNow}
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Train in Bulgaria Section */}
      <section className="why-bulgaria-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.benefits.heading}
          </motion.h2>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  className="benefit-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.05 }}
                >
                  <div className="benefit-icon">
                    <IconComponent size={48} color="#C49818" />
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Comparison Table Section */}
      <section className="comparison-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.comparison.heading}
          </motion.h2>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>{t.comparison.locationName}</th>
                  <th>{t.comparison.primarySports}</th>
                  <th>{t.comparison.capacity}</th>
                  <th>{t.comparison.indoorOutdoor}</th>
                  <th>{t.comparison.seasonAvailability}</th>
                  <th>{t.comparison.nearbyAccommodation}</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td><strong>{location.name}</strong><br /><small>{location.location}</small></td>
                    <td>{location.sports.join(', ')}</td>
                    <td>{location.capacity}</td>
                    <td>{location.id === 5 ? t.comparison.both : t.comparison.indoor}</td>
                    <td>{location.id === 4 ? t.comparison.basedOnAvailability : t.comparison.yearRound}</td>
                    <td>{t.comparison.yes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Comparison Cards */}
          <div className="comparison-table-mobile">
            {locations.map((location) => (
              <motion.div
                key={location.id}
                className="comparison-mobile-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h4>{location.name}</h4>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.locationLabel}</span>
                  <span className="comparison-mobile-card-value">{location.location}</span>
                </div>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.primarySportsLabel}</span>
                  <span className="comparison-mobile-card-value">{location.sports.join(', ')}</span>
                </div>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.capacityLabel}</span>
                  <span className="comparison-mobile-card-value">{location.capacity}</span>
                </div>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.indoorOutdoorLabel}</span>
                  <span className="comparison-mobile-card-value">{location.id === 5 ? t.comparison.both : t.comparison.indoor}</span>
                </div>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.seasonLabel}</span>
                  <span className="comparison-mobile-card-value">{location.id === 4 ? t.comparison.basedOnAvailability : t.comparison.yearRound}</span>
                </div>
                <div className="comparison-mobile-card-row">
                  <span className="comparison-mobile-card-label">{t.comparison.nearbyLabel}</span>
                  <span className="comparison-mobile-card-value">{t.comparison.yes}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="locations-cta-section">
        <div className="container">
          <motion.div
            className="locations-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{t.cta.heading}</h2>
            <p>{t.cta.text}</p>
            <div className="locations-cta-buttons">
              <Link to="/packages">
                <motion.button
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.viewPackages}
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.contactUs}
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Locations;
